import { useEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { BackLink } from '../components/BackLink';
import { PlaceholderScreen } from './PlaceholderScreen';
import { ReviewAudio } from '../components/ReviewAudio';
import { formatWhen } from './Auditions';
import {
  getAuditionDetail,
  transitionRecord,
  createAuditionSession,
  listAuditionTapes,
  uploadTape,
  createReviewer,
  listReviewers,
  revokeReviewer,
  getAuditionReviews,
  fetchMediaFile,
  parseReviewMarkers,
  shareablePortalUrl,
  ApiError,
  type AuditionDetail as AuditionDetailData,
  type AuditionTape,
  type ReviewerLink,
  type ReviewerRole,
  type CreatedReviewer,
  type AuditionReview,
  type RecordRow,
  type SessionModality,
} from '../lib/api';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { RevisionChip, ChangeFlag, currentSides } from './sidesPresentation';
import './AuditionDetail.css';

export interface AuditionDetailProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  // B3a provenance: opens the untouched source-document viewer for this
  // audition's linked breakdown. Absent-safe — the chip only renders when a
  // source is actually linked.
  onViewSource: (sourceId: string) => void;
}

// The six advanceable active states, in advancement order, PLUS the
// success exit `booked` — mirrors packages/verticals/actor/src/index.ts's
// own lifecycle.states minus `archived` (archived is rendered as its own
// action/revive row below, exactly like the blessed demo's aud1 stepper,
// which never includes "archived" among its 7 steps either).
const STEPPER_STATES = [
  'self_submitted',
  'audition',
  'callback',
  'chemistry_read',
  'screen_test',
  'pinned',
  'booked',
] as const;

const STEP_LABEL: Record<string, string> = {
  self_submitted: 'self-sub',
  audition: 'audition',
  callback: 'callback',
  chemistry_read: 'chem',
  screen_test: 'screen test',
  pinned: 'pinned',
  booked: 'booked',
};

// archived -> any ACTIVE state is legal (the pack's own revive rule) but
// NOT -> booked (you revive into an active stage, then book from there) --
// see actor pack's own transitions list.
const REVIVE_STATES = ['self_submitted', 'audition', 'callback', 'chemistry_read', 'screen_test', 'pinned'] as const;

const MODALITY_LABEL: Record<SessionModality, string> = {
  in_person: 'In person',
  zoom: 'Zoom',
  self_tape: 'Self-tape',
};

const MODALITY_CLASS: Record<SessionModality, string> = {
  in_person: 'audition-session--in-person',
  zoom: 'audition-session--zoom',
  self_tape: 'audition-session--self-tape',
};

const SESSION_KIND_LABEL: Record<string, string> = {
  audition: 'Audition',
  callback: 'Callback',
  chemistry_read: 'Chemistry read',
  screen_test: 'Screen test',
};

type LoadState = 'loading' | 'ready' | 'error';

function fieldText(record: RecordRow, name: string): string | null {
  const value = record.fields[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** What this modality actually does today (plugins/actorAuditions.ts's own
 * scheduling rule) -- self_tape truthfully gets a task+nag whenever it
 * carries a due date; zoom/in_person get no auto-scheduled artifact yet
 * (the B2 brief's own STOP-and-flag), so this never claims otherwise. */
function schedulingNote(session: RecordRow): string | null {
  const modality = session.fields['modality'];
  if (modality === 'self_tape') {
    return session.fields['scheduled_at'] ? 'Task + nag' : null;
  }
  return null;
}

function SessionRow({ session }: { session: RecordRow }) {
  const modality = session.fields['modality'] as SessionModality | undefined;
  const kind = SESSION_KIND_LABEL[session.fields['session_kind'] as string] ?? (session.fields['session_kind'] as string);
  const round = session.fields['round'];
  const when = formatWhen(session.fields['scheduled_at']);
  const note = schedulingNote(session);
  const prepNotes = fieldText(session, 'prep_notes');
  const locationOrLink = fieldText(session, 'location_or_link');
  const travelNeeded = session.fields['travel_needed'] === true;

  return (
    <div class={`audition-session ${modality ? MODALITY_CLASS[modality] : ''}`}>
      <div class="audition-session__k">
        {kind}
        {typeof round === 'number' && round > 1 ? ` · Round ${round}` : ''}
        {when ? ` · ${when}` : ''}
      </div>
      {(note || prepNotes || locationOrLink) && (
        <div class="audition-session__d">
          {[note, locationOrLink, prepNotes].filter(Boolean).join(' — ')}
        </div>
      )}
      {modality && (
        <div class="audition-session__mod">
          {MODALITY_LABEL[modality]}
          {travelNeeded ? ' · travel' : ''}
        </div>
      )}
    </div>
  );
}

interface AddSessionFormProps {
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: {
    sessionKind: string;
    round?: number;
    modality?: SessionModality;
    scheduledAt?: string;
    locationOrLink?: string;
    prepNotes?: string;
    travelNeeded?: boolean;
  }) => void;
}

function AddSessionForm({ saving, error, onCancel, onSave }: AddSessionFormProps) {
  const [sessionKind, setSessionKind] = useState('audition');
  const [round, setRound] = useState('1');
  const [modality, setModality] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [locationOrLink, setLocationOrLink] = useState('');
  const [prepNotes, setPrepNotes] = useState('');
  const [travelNeeded, setTravelNeeded] = useState(false);

  function submit(e: Event) {
    e.preventDefault();
    const parsedRound = Number.parseInt(round, 10);
    onSave({
      sessionKind,
      round: Number.isFinite(parsedRound) && parsedRound > 0 ? parsedRound : undefined,
      modality: modality ? (modality as SessionModality) : undefined,
      scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      locationOrLink: locationOrLink || undefined,
      prepNotes: prepNotes || undefined,
      travelNeeded: modality === 'in_person' ? travelNeeded : undefined,
    });
  }

  return (
    <form class="audition-add-session" onSubmit={submit}>
      <label class="audition-add-session__field">
        <span>Kind</span>
        <select value={sessionKind} onChange={(e) => setSessionKind((e.target as HTMLSelectElement).value)}>
          <option value="audition">Audition</option>
          <option value="callback">Callback</option>
          <option value="chemistry_read">Chemistry read</option>
          <option value="screen_test">Screen test</option>
        </select>
      </label>
      <label class="audition-add-session__field">
        <span>Round</span>
        <input type="number" min="1" value={round} onInput={(e) => setRound((e.target as HTMLInputElement).value)} />
      </label>
      <label class="audition-add-session__field">
        <span>Modality</span>
        <select value={modality} onChange={(e) => setModality((e.target as HTMLSelectElement).value)}>
          <option value="">—</option>
          <option value="self_tape">Self-tape</option>
          <option value="zoom">Zoom</option>
          <option value="in_person">In person</option>
        </select>
      </label>
      <label class="audition-add-session__field">
        <span>When</span>
        <input
          type="datetime-local"
          value={scheduledAt}
          onInput={(e) => setScheduledAt((e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="audition-add-session__field">
        <span>Location / link</span>
        <input
          type="text"
          value={locationOrLink}
          onInput={(e) => setLocationOrLink((e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="audition-add-session__field">
        <span>Prep notes</span>
        <input type="text" value={prepNotes} onInput={(e) => setPrepNotes((e.target as HTMLInputElement).value)} />
      </label>
      {modality === 'in_person' && (
        <label class="audition-add-session__checkbox">
          <input
            type="checkbox"
            checked={travelNeeded}
            onChange={(e) => setTravelNeeded((e.target as HTMLInputElement).checked)}
          />
          <span>Travel needed</span>
        </label>
      )}
      {error && <div class="audition-add-session__error">{error}</div>}
      <div class="audition-add-session__actions">
        <button type="submit" class="audition-add-session__save" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" class="audition-add-session__cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// The audition detail: stage stepper (advance/archive/revive), its
// sessions (modality colour-coded per the demo), casting director +
// linked project, and Materials (sides/tape — the tape send-for-notes
// feedback loop itself is B7, so Tape here is a scaffold-only read, never
// a fabricated record).
// B2.1: one key/value row of a breakdown section (the demo's `.kvline`);
// `mono` gold-tints machine-literal values (file labels, deadlines).
function KvLine({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div class="audition-kvline">
      <span class="audition-kvline__k">{k}</span>
      <span class={`audition-kvline__v${mono ? ' audition-kvline__v--mono' : ''}`}>{v}</span>
    </div>
  );
}

// B7 — mm:ss for a review's time marker chip.
function clock(t: number): string {
  const total = Math.max(0, Math.round(t));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const REVIEWER_ROLE_LABEL: Record<string, string> = {
  coach: 'coach',
  agent: 'agent',
  manager: 'manager',
  self: 'self',
  other: 'reviewer',
};

const REVIEWER_STATUS_LABEL: Record<string, string> = {
  active: 'link active',
  submitted: 'notes in',
  revoked: 'revoked',
  expired: 'expired',
};

// B7 — the current tape as a native <video> via the OBJECT-URL pattern (tenant-
// auth GET /api/actor/media/:id/file). A shared `videoRef` lets a received
// review's markers seek THIS element. Calm placeholder on load / on failure.
function TapeVideo({
  tenantId,
  tapeId,
  videoRef,
}: {
  tenantId: string;
  tapeId: string;
  videoRef: RefObject<HTMLVideoElement>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    setUrl(null);
    setFailed(false);
    void fetchMediaFile(tenantId, tapeId)
      .then((file) => {
        if (cancelled) {
          URL.revokeObjectURL(file.objectUrl);
          return;
        }
        revoke = file.objectUrl;
        setUrl(file.objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tenantId, tapeId]);

  if (failed) return <div class="audition-tape__fail">This tape couldn’t be loaded.</div>;
  if (!url) return <div class="audition-tape__loading" aria-hidden="true" />;
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- a self-tape has no
    // caption track; the surrounding UI names it.
    <video ref={videoRef} class="audition-tape__video" src={url} controls preload="metadata" playsInline />
  );
}

// B7 — clickable time markers on a received review; each seeks (and plays) the
// tape at its timestamp.
function ReviewMarkers({ raw, videoRef }: { raw: unknown; videoRef: RefObject<HTMLVideoElement> }) {
  const markers = parseReviewMarkers(raw);
  if (markers.length === 0) return null;
  function seek(t: number) {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    void v.play().catch(() => {});
  }
  return (
    <div class="audition-review__markers">
      {markers.map((m, i) => (
        <button type="button" class="audition-review__marker" key={i} onClick={() => seek(m.t)}>
          {clock(m.t)}
          {m.label ? ` · ${m.label}` : ''}
        </button>
      ))}
    </div>
  );
}

// B7 — the "send this tape for notes" panel (owner side): mint a scoped, private
// reviewer link (name + role), show it with a Copy control, and list every sent
// reviewer with status + a Revoke. Multiple reviewers supported.
function SendForNotes({
  tenantId,
  auditionId,
  reviewers,
  onChanged,
}: {
  tenantId: string;
  auditionId: string;
  reviewers: ReviewerLink[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState<ReviewerRole>('coach');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedReviewer | null>(null);
  const [copied, setCopied] = useState(false);

  async function create(e: Event) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setCopied(false);
    try {
      const result = await createReviewer(tenantId, auditionId, {
        reviewerName: name.trim() || undefined,
        reviewerRole: role,
      });
      setCreated(result);
      setName('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the link.');
    } finally {
      setSaving(false);
    }
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  async function revoke(grantId: string) {
    try {
      await revokeReviewer(tenantId, grantId);
      onChanged();
    } catch {
      /* a failed revoke leaves the list as-is; the owner can retry */
    }
  }

  const shareLink = created ? shareablePortalUrl(created.token) : '';

  return (
    <div class="audition-notes">
      {reviewers.length > 0 && (
        <div class="audition-notes__list">
          {reviewers.map((r) => (
            <div class="audition-notes__row" key={r.grantId} data-testid="reviewer-row">
              <div class="audition-notes__who">
                <span class="audition-notes__name">{r.reviewerName ?? 'A reviewer'}</span>
                {r.reviewerRole && (
                  <span class="audition-notes__role">{REVIEWER_ROLE_LABEL[r.reviewerRole] ?? r.reviewerRole}</span>
                )}
              </div>
              <span class={`audition-notes__status audition-notes__status--${r.status}`}>
                {REVIEWER_STATUS_LABEL[r.status] ?? r.status}
              </span>
              {(r.status === 'active' || r.status === 'submitted') && (
                <button type="button" class="audition-notes__revoke" onClick={() => void revoke(r.grantId)}>
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {created && (
        <div class="audition-notes__created" data-testid="created-link">
          <div class="audition-notes__calm">A private link — no account needed. Send it to your reviewer.</div>
          <div class="audition-notes__linkrow">
            <input class="audition-notes__link" type="text" readOnly value={shareLink} aria-label="Private review link" />
            <button type="button" class="audition-notes__copy" onClick={() => void copyLink(shareLink)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {open ? (
        <form class="audition-notes__form" onSubmit={create}>
          <label class="audition-notes__field">
            <span>Reviewer name</span>
            <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </label>
          <label class="audition-notes__field">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole((e.target as HTMLSelectElement).value as ReviewerRole)}>
              <option value="coach">Coach</option>
              <option value="agent">Agent</option>
              <option value="manager">Manager</option>
              <option value="other">Other</option>
            </select>
          </label>
          {error && <div class="audition-notes__error">{error}</div>}
          <div class="audition-notes__actions">
            <button type="submit" class="audition-notes__save" disabled={saving}>
              {saving ? 'Creating…' : 'Create link'}
            </button>
            <button
              type="button"
              class="audition-notes__cancel"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              disabled={saving}
            >
              Done
            </button>
          </div>
        </form>
      ) : (
        <button type="button" class="audition-notes__open" onClick={() => setOpen(true)}>
          Send for feedback
        </button>
      )}
    </div>
  );
}

// B7 — the received reviews for an audition: each reviewer's written notes,
// their recorded audio (tenant-auth), the transcript, and clickable markers that
// seek the tape. Multi-reviewer.
function ReviewsSection({
  tenantId,
  reviews,
  videoRef,
}: {
  tenantId: string;
  reviews: AuditionReview[];
  videoRef: RefObject<HTMLVideoElement>;
}) {
  const submitted = reviews.filter((r) => r.status !== 'draft');
  if (submitted.length === 0) {
    return <div class="audition-detail__empty">No notes in yet.</div>;
  }
  return (
    <>
      {submitted.map((r) => {
        const role = r.reviewerRole ? REVIEWER_ROLE_LABEL[r.reviewerRole] ?? r.reviewerRole : null;
        return (
          <div class="audition-review" key={r.id} data-testid="review-card">
            <div class="audition-review__top">
              <span class="audition-review__name">{r.reviewerName ?? 'A reviewer'}</span>
              {role && <span class="audition-review__role">{role}</span>}
            </div>
            {r.body && <div class="audition-review__body">{r.body}</div>}
            {r.audioFileUrl && <ReviewAudio tenantId={tenantId} reviewId={r.id} eager />}
            <ReviewMarkers raw={r.timeMarkers} videoRef={videoRef} />
            {r.transcript && (
              <div class="audition-review__transcript">
                <span class="audition-review__transcript-lab">Transcript</span>
                {r.transcript}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export function AuditionDetail({ tenantId, id, onBack, onViewSource }: AuditionDetailProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [detail, setDetail] = useState<AuditionDetailData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [showAddSession, setShowAddSession] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [addSessionError, setAddSessionError] = useState<string | null>(null);
  // B7 — tapes / reviewer links / received reviews for this audition.
  const [tapes, setTapes] = useState<AuditionTape[]>([]);
  const [reviewers, setReviewers] = useState<ReviewerLink[]>([]);
  const [reviews, setReviews] = useState<AuditionReview[]>([]);
  const [uploadingTape, setUploadingTape] = useState(false);
  const [tapeError, setTapeError] = useState<string | null>(null);
  const tapeVideoRef = useRef<HTMLVideoElement>(null);
  // R3 — reload the composed detail after an edit/act on this audition.
  const recordsVersion = useRecordsVersion();

  async function load() {
    setState('loading');
    try {
      const data = await getAuditionDetail(tenantId, id);
      setDetail(data);
      setState('ready');
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
      setState('error');
    }
  }

  // B7 — the tape/reviewer/review data loads independently of the core detail so
  // a hiccup in one never blanks the whole screen (each degrades to its own
  // empty). Reloaded after an upload / a new or revoked reviewer link.
  async function loadFeedback() {
    const [t, rv, rw] = await Promise.allSettled([
      listAuditionTapes(tenantId, id),
      listReviewers(tenantId, id),
      getAuditionReviews(tenantId, id),
    ]);
    if (t.status === 'fulfilled') setTapes(t.value);
    if (rv.status === 'fulfilled') setReviewers(rv.value);
    if (rw.status === 'fulfilled') setReviews(rw.value);
  }

  useEffect(() => {
    void load();
    void loadFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, recordsVersion]);

  async function onPickTape(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setUploadingTape(true);
    setTapeError(null);
    try {
      await uploadTape(tenantId, id, file);
      await loadFeedback();
    } catch (err) {
      setTapeError(err instanceof ApiError ? err.message : 'Could not upload the tape.');
    } finally {
      setUploadingTape(false);
      input.value = '';
    }
  }

  async function advance(to: string) {
    setTransitionError(null);
    try {
      await transitionRecord(tenantId, id, to);
      await load();
    } catch (err) {
      setTransitionError(err instanceof ApiError ? err.message : 'Could not update the stage.');
    }
  }

  async function saveSession(input: Parameters<AddSessionFormProps['onSave']>[0]) {
    setSavingSession(true);
    setAddSessionError(null);
    try {
      await createAuditionSession(tenantId, id, input);
      setShowAddSession(false);
      await load();
    } catch (err) {
      setAddSessionError(err instanceof ApiError ? err.message : 'Could not add the session.');
    } finally {
      setSavingSession(false);
    }
  }

  if (state === 'loading') {
    return (
      <div class="audition-detail">
        <BackLink label="Auditions" onClick={onBack} />
        <PlaceholderScreen eyebrow="Audition" line="One moment…" />
      </div>
    );
  }

  if (state === 'error' || !detail) {
    return (
      <div class="audition-detail">
        <BackLink label="Auditions" onClick={onBack} />
        <PlaceholderScreen eyebrow="Audition" line="Couldn't load this audition." soft={errorMessage ?? undefined} />
      </div>
    );
  }

  const { audition, sessions, castingDirector, project } = detail;
  const source = detail.source ?? null;
  const projectName = fieldText(audition, 'project') ?? audition.display_name;
  const role = fieldText(audition, 'role');
  const office = fieldText(audition, 'office');
  const currentState = audition.lifecycle_state;
  const isArchived = currentState === 'archived';
  const isBooked = currentState === 'booked';
  const currentIndex = STEPPER_STATES.indexOf((currentState ?? '') as (typeof STEPPER_STATES)[number]);
  const sidesText = fieldText(audition, 'sides');
  // B6c-2: the audition's real sides revisions — the current one (colour chip
  // + calm change flag, opening its ORIGINAL) surfaces in Materials below.
  const sidesRevisions = detail.sides ?? [];
  const currentRevision = currentSides(sidesRevisions);

  // B2.1 full-breakdown fields — every one optional; a section renders only
  // when its own field is non-empty (nothing fabricated for absent data).
  const projectDescription = fieldText(audition, 'project_description');
  const characterName = fieldText(audition, 'character_name');
  const characterBreakdown = fieldText(audition, 'character_breakdown');
  const castingNotes = fieldText(audition, 'casting_notes');
  const performanceNotes = fieldText(audition, 'performance_notes');
  const toneNotes = fieldText(audition, 'tone_notes');
  const referenceLinks = fieldText(audition, 'reference_links');
  const avoidNotes = fieldText(audition, 'avoid_notes');
  const scriptText = fieldText(audition, 'script_text');
  const submissionDeadline = formatWhen(audition.fields['submission_deadline']);
  const slateInstruction = fieldText(audition, 'slate_instruction');
  const fileLabelConvention = fieldText(audition, 'file_label_convention');
  const uploadTarget = fieldText(audition, 'upload_target');
  const takesFormat = fieldText(audition, 'takes_format');
  const hasSubmissionBlock = Boolean(
    submissionDeadline || slateInstruction || fileLabelConvention || uploadTarget || takesFormat
  );

  const metaParts: string[] = [];
  if (castingDirector) metaParts.push(`CD ${fieldText(castingDirector, 'full_name') ?? castingDirector.display_name}`);
  if (office) metaParts.push(office);
  if (project) metaParts.push(`project: ${fieldText(project, 'title') ?? project.display_name}`);

  return (
    <div class="audition-detail">
      <BackLink label="Auditions" onClick={onBack} />
      <div class="audition-detail__title">
        {projectName}
        {role ? ` — ${role}` : ''}
      </div>
      {metaParts.length > 0 && <div class="audition-detail__meta">{metaParts.join(' · ')}</div>}
      <div class="audition-detail__edit-row">
        <button
          type="button"
          class="detail-edit-btn"
          onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'actor_audition', id })}
          data-testid="edit-audition"
        >
          Edit details
        </button>
      </div>
      {source && (
        <div class="audition-detail__srclink-row">
          <button type="button" class="srclink" onClick={() => onViewSource(source.id)}>
            <span class="srclink__pin" aria-hidden="true">
              📄
            </span>
            View original breakdown
          </button>
        </div>
      )}

      {!isArchived && (
        <div class="audition-detail__stepper" role="list">
          {STEPPER_STATES.map((step, index) => {
            const isDone = currentIndex >= 0 && index < currentIndex;
            const isAt = step === currentState;
            const clickable = !isDone && !isAt && currentIndex >= 0 && index > currentIndex;
            return (
              <button
                type="button"
                key={step}
                role="listitem"
                class={`audition-step${isDone ? ' audition-step--done' : ''}${isAt ? ' audition-step--at' : ''}`}
                disabled={!clickable}
                onClick={clickable ? () => void advance(step) : undefined}
              >
                {STEP_LABEL[step]}
              </button>
            );
          })}
        </div>
      )}

      {isArchived ? (
        <div class="audition-detail__archive-row">
          <span class="audition-detail__archived-note">Archived</span>
          {REVIVE_STATES.map((step) => (
            <button type="button" key={step} class="audition-step" onClick={() => void advance(step)}>
              {STEP_LABEL[step]}
            </button>
          ))}
        </div>
      ) : (
        !isBooked && (
          <button type="button" class="audition-detail__archive-btn" onClick={() => void advance('archived')}>
            Archive
          </button>
        )
      )}

      {transitionError && <div class="audition-detail__error">{transitionError}</div>}

      {/* -- B2.1 full breakdown (each block optional) -- */}
      {projectDescription && (
        <>
          <div class="audition-detail__section-label">The project</div>
          <div class="audition-bd">{projectDescription}</div>
        </>
      )}

      {(characterName || characterBreakdown || castingNotes) && (
        <>
          <div class="audition-detail__section-label">The role</div>
          {characterName && <KvLine k="Character" v={characterName} />}
          {castingNotes && <KvLine k="Casting" v={castingNotes} />}
          {characterBreakdown && <div class="audition-bd">{characterBreakdown}</div>}
        </>
      )}

      {(performanceNotes || toneNotes || referenceLinks) && (
        <>
          <div class="audition-detail__section-label">Performance notes</div>
          {performanceNotes && <div class="audition-bd">{performanceNotes}</div>}
          {toneNotes && <div class="audition-bd audition-bd--mut">{toneNotes}</div>}
          {referenceLinks && <div class="audition-bd audition-bd--mut audition-bd--ref">{referenceLinks}</div>}
        </>
      )}

      {avoidNotes && (
        <>
          <div class="audition-detail__section-label audition-detail__section-label--avoid">Avoid</div>
          <div class="audition-avoidbox">{avoidNotes}</div>
        </>
      )}

      {scriptText && (
        <>
          <div class="audition-detail__section-label">Script</div>
          <div class="audition-script">{scriptText}</div>
        </>
      )}

      {hasSubmissionBlock && (
        <>
          <div class="audition-detail__section-label">Submission</div>
          {submissionDeadline && <KvLine k="Deadline" v={submissionDeadline} mono />}
          {slateInstruction && <KvLine k="Slate" v={slateInstruction} />}
          {takesFormat && <KvLine k="Takes" v={takesFormat} />}
          {fileLabelConvention && <KvLine k="File label" v={fileLabelConvention} mono />}
          {uploadTarget && <KvLine k="Upload to" v={uploadTarget} />}
        </>
      )}

      <div class="audition-detail__section-label">Sessions</div>
      {sessions.length === 0 ? (
        <div class="audition-detail__empty">No sessions yet.</div>
      ) : (
        sessions.map((session) => <SessionRow key={session.id} session={session} />)
      )}

      {showAddSession ? (
        <AddSessionForm
          saving={savingSession}
          error={addSessionError}
          onCancel={() => {
            setShowAddSession(false);
            setAddSessionError(null);
          }}
          onSave={(input) => void saveSession(input)}
        />
      ) : (
        <button type="button" class="audition-detail__add-session" onClick={() => setShowAddSession(true)}>
          + Add session
        </button>
      )}

      <div class="audition-detail__section-label">Materials</div>
      {source && (
        <div class="audition-detail__kv">
          <span class="audition-detail__kv-k">Original breakdown</span>
          <button type="button" class="audition-detail__kv-src" onClick={() => onViewSource(source.id)}>
            {source.filename ?? source.kind ?? 'source'} · view source ›
          </button>
        </div>
      )}
      {currentRevision ? (
        <div class="audition-sides">
          <div class="audition-sides__top">
            <span class="audition-detail__kv-k">Sides</span>
            <RevisionChip sides={currentRevision} onViewSource={onViewSource} />
          </div>
          {currentRevision.supersedesId !== null && <ChangeFlag sides={currentRevision} />}
          {fieldText(currentRevision, 'pages_or_scenes_affected') && (
            <div class="audition-sides__pages">
              Pages / scenes: {fieldText(currentRevision, 'pages_or_scenes_affected')}
            </div>
          )}
        </div>
      ) : (
        <div class="audition-detail__kv">
          <span class="audition-detail__kv-k">Sides</span>
          <span class="audition-detail__kv-v">{sidesText ?? 'None attached yet'}</span>
        </div>
      )}
      <div class="audition-tape">
        <div class="audition-tape__top">
          <span class="audition-detail__kv-k">Tape</span>
          <label class={`audition-tape__add${uploadingTape ? ' audition-tape__add--busy' : ''}`}>
            {uploadingTape ? 'Uploading…' : '+ Add tape'}
            <input
              type="file"
              accept="video/*"
              class="audition-tape__file"
              disabled={uploadingTape}
              onChange={(e) => void onPickTape(e)}
            />
          </label>
        </div>
        {tapeError && <div class="audition-detail__error">{tapeError}</div>}
        {tapes.length === 0 ? (
          <div class="audition-detail__empty">No tape yet.</div>
        ) : (
          <>
            <TapeVideo tenantId={tenantId} tapeId={tapes[tapes.length - 1]!.id} videoRef={tapeVideoRef} />
            {tapes.length > 1 && (
              <div class="audition-tape__count">
                Showing your latest of {tapes.length} tapes.
              </div>
            )}
          </>
        )}
      </div>

      {tapes.length > 0 && (
        <>
          <div class="audition-detail__section-label">Send for notes</div>
          <SendForNotes tenantId={tenantId} auditionId={id} reviewers={reviewers} onChanged={() => void loadFeedback()} />
        </>
      )}

      <div class="audition-detail__section-label">Reviews</div>
      <ReviewsSection tenantId={tenantId} reviews={reviews} videoRef={tapeVideoRef} />
    </div>
  );
}
